/**
 * "Who is who": an owner of a match matches each block's players to the
 * reference block's (shown as A..D), one yes/no question at a time, and says
 * which blocks have other people in them. The result is the players_mapping
 * their own report is corrected with (PUT /statistics/{session}/identification).
 *
 * Pure state machine, driven by IdentifyPlayersFlow:
 * - The reference is the first block. If its players didn't play, the next
 *   block takes over.
 * - For each other block, in time order, the reference players are asked in
 *   turn ("is it the same person?") against that block's players, the ones on
 *   the same court side first. The last player is whoever is left.
 * - "Didn't play" under either image, or No to every candidate, asks to
 *   confirm and leaves that block (or the reference) out.
 * - Every step can be undone.
 */
/** A block of the session's statistics, as the owner tells who is who in it. */
export interface IdentificationSegment {
  /** Position in the statistics' segments_statistics. */
  index: number;
  start_time: number;
  end_time: number;
  total_game_time: number;
  /** player1..player4 → crop (base64 JPEG). */
  player_crops: Record<string, string>;
  /** player1..player4 → court side. */
  roles: Record<string, 'left' | 'right'>;
}

export type IdentifyPhase = 'ask' | 'notPlayed' | 'notice' | 'review' | 'empty';

/** Why "did this person play?" opened: No to every candidate, or "didn't play" under an image. */
export type NotPlayedReason = 'all' | 'reference' | 'candidate';

interface Snapshot {
  phase: IdentifyPhase;
  /** The reference block: its players are A..D (player1..player4). */
  reference: number;
  /** The blocks to identify against it, in time order. */
  order: number[];
  /** The block being identified. */
  current: number;
  /** The reference player (1..4) being looked for in it. */
  player: number;
  /** Index into his candidates (see candidates()). */
  candidate: number;
  /** Per block, per local slot (0..3): the reference player id (1..4), 0 while unknown. */
  assign: number[][];
  /** Blocks left out: other people in them, or the reference's players didn't play. */
  excluded: boolean[];
  reason: NotPlayedReason | null;
  /** The candidate slot "didn't play" was said about. */
  reasonSlot: number | null;
  /** After a block is left out: whether it was the first or the last one (explains why). */
  notice: 'first' | 'last' | null;
  /** What Continuar applies after the notice. */
  pending: Partial<Snapshot> | null;
  /** Back on a "did they play?" confirmation through Undo: they had said No. */
  revisiting: boolean;
}

export interface IdentifyState extends Snapshot {
  /** Every earlier state, for Undo. */
  history: Snapshot[];
}

export type IdentifyAction =
  | { type: 'yes' }
  | { type: 'no' }
  | { type: 'notPlayed'; who: 'reference' | 'candidate' }
  | { type: 'confirmNotPlayed' }
  | { type: 'continue' }
  | { type: 'undo' }
  | { type: 'restart' };

/** Identification of every other kept block against `reference`, from the start. */
function start(
  segments: IdentificationSegment[],
  reference: number,
  excluded: boolean[],
): Snapshot {
  const order = segments
    .map((_, i) => i)
    .filter((i) => i !== reference && !excluded[i]);
  return {
    phase: order.length ? 'ask' : 'review',
    reference,
    order,
    current: order[0] ?? reference,
    player: 1,
    candidate: 0,
    assign: segments.map((_, i) =>
      i === reference ? [1, 2, 3, 4] : [0, 0, 0, 0],
    ),
    excluded,
    reason: null,
    reasonSlot: null,
    notice: null,
    pending: null,
    revisiting: false,
  };
}

export function initialIdentifyState(
  segments: IdentificationSegment[],
): IdentifyState {
  const excluded = segments.map(() => false);
  const snapshot = segments.length
    ? start(segments, 0, excluded)
    : { ...start(segments, 0, excluded), phase: 'empty' as const };
  return { ...snapshot, history: [] };
}

/** The current block's unmatched slots, the ones on the reference player's court side first. */
export function candidates(
  segments: IdentificationSegment[],
  state: Snapshot,
): number[] {
  const side = segments[state.reference].roles[`player${state.player}`];
  const roles = segments[state.current].roles;
  const rank = (slot: number) => (roles[`player${slot + 1}`] === side ? 0 : 1);
  return [0, 1, 2, 3]
    .filter((slot) => state.assign[state.current][slot] === 0)
    .sort((a, b) => rank(a) - rank(b));
}

/** Leaving out the first or the last block gets an explanation. */
function edge(
  segments: IdentificationSegment[],
  block: number,
): 'first' | 'last' | null {
  if (block === 0) return 'first';
  if (block === segments.length - 1) return 'last';
  return null;
}

/** Apply a change, remembering the current state for Undo. */
function go(state: IdentifyState, change: Partial<Snapshot>): IdentifyState {
  const { history, ...current } = state;
  return {
    ...current,
    revisiting: false,
    ...change,
    history: [...history, current],
  };
}

/** What starts the block after the current one, or the review after the last. */
function next(state: Snapshot, extra: Partial<Snapshot>): Partial<Snapshot> {
  const position = state.order.indexOf(state.current);
  if (position === state.order.length - 1) return { ...extra, phase: 'review' };
  return {
    ...extra,
    phase: 'ask',
    current: state.order[position + 1],
    player: 1,
    candidate: 0,
  };
}

const withRow = (assign: number[][], block: number, row: number[]) =>
  assign.map((r, i) => (i === block ? row : r));

export function identifyReducer(
  segments: IdentificationSegment[],
  state: IdentifyState,
  action: IdentifyAction,
): IdentifyState {
  switch (action.type) {
    case 'yes': {
      const slot = candidates(segments, state)[state.candidate];
      const row = [...state.assign[state.current]];
      row[slot] = state.player;
      const freeSlots = [0, 1, 2, 3].filter((s) => row[s] === 0);
      const freeIds = [1, 2, 3, 4].filter((id) => !row.includes(id));
      if (freeSlots.length === 1) {
        // The last player is whoever is left; on to the next block.
        row[freeSlots[0]] = freeIds[0];
        return go(
          state,
          next(state, { assign: withRow(state.assign, state.current, row) }),
        );
      }
      return go(state, {
        assign: withRow(state.assign, state.current, row),
        player: freeIds[0],
        candidate: 0,
      });
    }
    case 'no':
      if (state.candidate + 1 >= candidates(segments, state).length) {
        return go(state, {
          phase: 'notPlayed',
          reason: 'all',
          reasonSlot: null,
        });
      }
      return go(state, { candidate: state.candidate + 1 });
    case 'notPlayed':
      return go(state, {
        phase: 'notPlayed',
        reason: action.who,
        reasonSlot:
          action.who === 'candidate'
            ? candidates(segments, state)[state.candidate]
            : null,
      });
    case 'confirmNotPlayed': {
      const excluded = [...state.excluded];
      if (state.reason === 'reference') {
        // The reference players didn't play: start over from the next block.
        // Blocks already left out stay out; their people didn't play either.
        excluded[state.reference] = true;
        const reference = excluded.indexOf(false);
        return go(state, {
          phase: 'notice',
          notice: edge(segments, state.reference),
          pending:
            reference < 0
              ? { phase: 'empty', excluded }
              : start(segments, reference, excluded),
        });
      }
      excluded[state.current] = true;
      return go(state, {
        phase: 'notice',
        notice: edge(segments, state.current),
        pending: next(state, {
          excluded,
          assign: withRow(state.assign, state.current, [0, 0, 0, 0]),
        }),
      });
    }
    case 'continue':
      return go(state, { ...state.pending, pending: null });
    case 'undo': {
      if (!state.history.length) return state;
      let history = state.history.slice(0, -1);
      let previous = state.history[state.history.length - 1];
      // Back over a block left out: past its "Confirmado" notice, onto the
      // confirmation they answered No to, which says so.
      if (previous.phase === 'notice' && history.length) {
        previous = history[history.length - 1];
        history = history.slice(0, -1);
      }
      return {
        ...previous,
        revisiting: previous.phase === 'notPlayed',
        history,
      };
    }
    case 'restart':
      return initialIdentifyState(segments);
  }
}

/**
 * The players_mapping to save: per block, its local player id (1..4) → the
 * reference player id (1..4); null for a block left out.
 */
export function playersMapping(
  segments: IdentificationSegment[],
  state: Snapshot,
): (Record<number, number> | null)[] {
  return segments.map((_, i) =>
    state.excluded[i] || state.assign[i].includes(0)
      ? null
      : Object.fromEntries(state.assign[i].map((id, slot) => [slot + 1, id])),
  );
}

/** The review: per reference player (A..D), their crop in every kept block, in time order. */
export function reviewColumns(
  segments: IdentificationSegment[],
  state: Snapshot,
): string[][] {
  const kept = segments
    .map((_, i) => i)
    .filter((i) => !state.excluded[i] && !state.assign[i].includes(0));
  return [1, 2, 3, 4].map((id) =>
    kept.map(
      (i) =>
        segments[i].player_crops[`player${state.assign[i].indexOf(id) + 1}`],
    ),
  );
}
